"use client";
/* eslint-disable @typescript-eslint/no-explicit-any, react-hooks/set-state-in-effect */

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { pageVariants, cardVariants, slideFromBottomVariants, headerVariants } from "@/lib/animations";
import TourRouteMap, { type TourRouteMapRef } from "@/components/ui/TourRouteMap";
import TourRouteMapLegend from "@/components/ui/TourRouteMapLegend";
import InteractiveMapStory from "@/components/ui/InteractiveMapStory";
import { buildDayFirstMapIndex, buildTourMapLocations, firstNonEmpty } from "@/utils/buildTourMapLocations";
import LazyImage from "@/components/ui/LazyImage";
import Faqs from "@/components/sections/Faqs";
import ProductShareButton from "@/components/ui/ProductShareButton";
import ProductDetailMenu, { type MenuItems } from "@/components/ui/ProductDetailMenu";
import SectionTitle from "@/components/ui/SectionTitle";
import YachtRouteCard, { type YachtRouteProps } from "@/components/ui/YachtRouteCard";
import { useProductYachtRouteData } from "@/hooks/useProductYachtRouteData";
import { useProductBreadcrumbs } from "@/hooks/useProductBreadcrumbs";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { transformMediaUrl } from "@/lib/media-url-transformer";
import type { FaqEntry } from "@/types/faq";
import type { PageUrlMap } from "@/types/blog";
import { sanitizeCmsHtml } from "@/lib/sanitize-cms-html";

interface ProductYachtRouteDetailProps {
    product?: {
        id?: number;
        name?: string;
        type?: string;
        product_type?: string;
        description?: string;
        gallery?: Array<any>;
        metas?: Record<string, any>;
        fields?: Record<string, any>;
        categories?: Array<{
            id?: number;
            name?: string;
            slug?: string;
        }>;
    };
    language?: string;
    languageId?: number;
    translations?: Record<string, string>;
    productFaqs?: FaqEntry[];
    pageUrls?: PageUrlMap;
    defaultLanguage?: string;
    menuItems?: MenuItems[];
}

export default function ProductYachtRouteDetail({
    product,
    language,
    languageId,
    translations = {},
    productFaqs = [],
    pageUrls,
    defaultLanguage,
    menuItems: propMenuItems,
}: ProductYachtRouteDetailProps = {}) {
    const [isFavorite, setIsFavorite] = useState(false);
    const [isStoryModeActive, setIsStoryModeActive] = useState(false);
    const [mapActiveIndex, setMapActiveIndex] = useState<number | null>(null);
    const [openRouteDay, setOpenRouteDay] = useState<string | null>(null);
    const [otherRoutes, setOtherRoutes] = useState<YachtRouteProps[]>([]);
    const [otherRoutesLoading, setOtherRoutesLoading] = useState(false);
    const [allExpanded, setAllExpanded] = useState(false);
    const mapRef = useRef<TourRouteMapRef>(null);
    const mapSectionRef = useRef<HTMLDivElement>(null);

    const { getVariable } = useLanguageVariables();
    const currentLanguage = language || getVariable('language', 'en');

    const routeData = useProductYachtRouteData(product, currentLanguage);
    const { breadcrumbs } = useProductBreadcrumbs(product, currentLanguage, true, pageUrls, defaultLanguage);

    const t = useCallback((key: string, fallback?: string): string => {
        return translations[key] || getVariable(key, fallback || key);
    }, [translations, getVariable]);


    useEffect(() => {
        if (!product?.id) return;
        const favorites = JSON.parse(localStorage.getItem('yacht_route_favorites') || '[]');
        setIsFavorite(favorites.includes(product.id));
    }, [product?.id]);

    const toggleFavorite = useCallback(() => {
        if (!product?.id) return;
        const favorites = JSON.parse(localStorage.getItem('yacht_route_favorites') || '[]');
        
        if (isFavorite) {
            const updated = favorites.filter((id: number) => id !== product.id);
            localStorage.setItem('yacht_route_favorites', JSON.stringify(updated));
            setIsFavorite(false);
        } else {
            favorites.push(product.id);
            localStorage.setItem('yacht_route_favorites', JSON.stringify(favorites));
            setIsFavorite(true);
        }
    }, [isFavorite, product?.id]);

    const transformHtmlImageUrls = useCallback((html: string): string => {
        if (!html) return html;
        return html.replace(/<img([^>]*?)src=["']([^"']+?)["']([^>]*?)>/gi, (match, beforeSrc, srcUrl, afterSrc) => {
            const transformedUrl = transformMediaUrl(srcUrl);
            return `<img${beforeSrc}src="${transformedUrl}"${afterSrc}>`;
        });
    }, []);

    const hasValidCoordinates = useCallback((lat: any, lng: any): boolean => {
        return lat !== undefined && lat !== null && !isNaN(Number(lat)) && isFinite(Number(lat)) &&
               lng !== undefined && lng !== null && !isNaN(Number(lng)) && isFinite(Number(lng));
    }, []);

    const hasLocationData = useCallback((day: any): boolean => {
        return hasValidCoordinates(day.lat, day.lng) || 
               (day.location_address && day.location_address.trim().length > 0);
    }, [hasValidCoordinates]);

    const routeDaysWithImages = useMemo(() => 
        routeData.routeProgram.map(day => ({
            ...day,
            description: day.description ? transformHtmlImageUrls(day.description) : day.description,
        })), 
        [routeData.routeProgram, transformHtmlImageUrls]
    );

    const mapDays = useMemo(() => 
        routeDaysWithImages.filter(hasLocationData), 
        [routeDaysWithImages, hasLocationData]
    );

    const mapLocations = useMemo(
        () => buildTourMapLocations(mapDays),
        [mapDays],
    );

    const dayFirstMapIndex = useMemo(
        () => buildDayFirstMapIndex(mapLocations),
        [mapLocations],
    );

    const mapStoryPoints = useMemo(() => {
        const dayById = new Map(mapDays.map((day) => [day.id, day]));
        return mapLocations.map((loc, index) => {
            const day = loc.day_id ? dayById.get(loc.day_id) : undefined;
            const description = firstNonEmpty(
                day?.description,
                loc.location_description,
            ) ?? '';
            return {
                id: `${loc.day_id}-${index}`,
                lat: loc.lat ?? 0,
                lng: loc.lng ?? 0,
                title: loc.location_title,
                description,
                image: loc.image ?? (typeof day?.images?.[0] === 'string' ? day.images[0] : undefined) ?? '',
                day: loc.day_number,
                stopIndex: loc.order_index,
                dayStopIndex: loc.day_stop_index,
                dayStopTotal: loc.day_stop_total,
                dayId: loc.day_id,
            };
        });
    }, [mapLocations, mapDays]);

    const hasMapLocations = mapLocations.length > 0;

    const highlightedDayId = mapActiveIndex != null
        ? mapLocations[mapActiveIndex]?.day_id ?? null
        : null;

    const scrollToRouteDay = useCallback((dayId: string) => {
        setAllExpanded(false);
        setOpenRouteDay(dayId);
        requestAnimationFrame(() => {
            document.querySelector(`[data-day-id="${dayId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }, []);

    const handleMapMarkerClick = useCallback((index: number) => {
        const loc = mapLocations[index];
        if (!loc) return;
        setMapActiveIndex(index);
        mapRef.current?.focusMarkerIndex(index);
        if (loc.day_id) scrollToRouteDay(loc.day_id);
    }, [mapLocations, scrollToRouteDay]);

    const focusMapOnDay = useCallback((dayId: string) => {
        const idx = dayFirstMapIndex.get(dayId);
        if (idx == null) return;
        setMapActiveIndex(idx);
        mapRef.current?.focusMarkerIndex(idx);
        if (typeof window !== 'undefined' && window.innerWidth < 1024) {
            mapSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [dayFirstMapIndex]);

    const handleStoryModeChange = useCallback((active: boolean) => {
        setIsStoryModeActive(active);
        setMapActiveIndex(active && mapStoryPoints.length > 0 ? 0 : null);
    }, [mapStoryPoints.length]);

    const handleStoryIndexChange = useCallback((index: number) => {
        setMapActiveIndex(index);
        mapRef.current?.focusMarkerIndex(index);
        const point = mapStoryPoints[index];
        if (point?.dayId) scrollToRouteDay(point.dayId);
    }, [mapStoryPoints, scrollToRouteDay]);

    const handleRouteDayToggle = useCallback((id: string) => {
        setOpenRouteDay(prev => prev === id ? null : id);
    }, []);

    const handleExpandAll = useCallback(() => {
        if (allExpanded) {
            setOpenRouteDay(null);
            setAllExpanded(false);
        } else {
            const lastDayId = routeDaysWithImages[routeDaysWithImages.length - 1]?.id;
            if (lastDayId) setOpenRouteDay(lastDayId);
            setAllExpanded(true);
        }
    }, [allExpanded, routeDaysWithImages]);

    // Diğer Rotalar - aynı dilde diğer yacht route'ları çek (mevcut hariç)
    useEffect(() => {
        if (!product?.id || !currentLanguage) return;
        const abort = new AbortController();
        const load = async () => {
            setOtherRoutesLoading(true);
            try {
                const params = new URLSearchParams();
                params.set('type', 'yacht_routes');
                params.set('language', currentLanguage);
                params.set('limit', '4');
                const res = await fetch(`/api/products?${params.toString()}`, { signal: abort.signal });
                if (!res.ok) return;
                const data = await res.json();
                const list: any[] = data?.data || [];
                const excludeId = product.id;
                const filtered = list.filter((p: any) => p.id !== excludeId).slice(0, 3);
                const transformed: YachtRouteProps[] = filtered.map((p: any) => {
                    const gallery = p.gallery || [];
                    const images: string[] = [];
                    const alts: string[] = [];
                    if (Array.isArray(gallery)) {
                        gallery.forEach((item: any) => {
                            if (item?.type === 'image' && item?.url) {
                                images.push(item.url);
                                alts.push(item.alt || p.name || '');
                            }
                        });
                    }
                    const fields = p.fields || {};
                    const metas = p.metas || {};
                    const rawDesc = (typeof p.description === 'string' ? p.description.replace(/<[^>]*>/g, '').trim().slice(0, 200) : '') || metas.route_highlights || fields.route_highlights?.field_value || '';
                    const cleaned = typeof rawDesc === 'string' ? rawDesc.replace(/<[^>]*>/g, '').trim() : '';
                    const shortDescription = cleaned.length > 160 ? cleaned.slice(0, 160) + '...' : cleaned;
                    return {
                        id: p.id,
                        src: images,
                        alt: alts,
                        imageCount: images.length,
                        videoCount: 0,
                        title: p.name || metas.route_name || fields.route_name?.field_value || '',
                        slug: p.slug,
                        url: undefined,
                        routeName: metas.route_name || fields.route_name?.field_value,
                        startLocation: metas.start_location || fields.start_location?.field_value || p.start_location,
                        endLocation: metas.end_location || fields.end_location?.field_value || p.end_location,
                        durationHours: metas.duration_hours ? parseFloat(metas.duration_hours) : (fields.duration_hours?.field_value ? parseFloat(fields.duration_hours.field_value) : undefined),
                        durationDays: metas.duration_days ? parseInt(metas.duration_days) : (fields.duration_days?.field_value ? parseInt(fields.duration_days.field_value) : undefined),
                        routeType: metas.route_type || fields.route_type?.field_value,
                        routeHighlights: metas.route_highlights || fields.route_highlights?.field_value,
                        shortDescription: shortDescription || undefined,
                    };
                });
                setOtherRoutes(transformed);
            } catch {
                setOtherRoutes([]);
            } finally {
                setOtherRoutesLoading(false);
            }
        };
        load().catch(() => { /* Abort/unmount - unhandled rejection önlenir */ });
        return () => abort.abort();
    }, [product?.id, currentLanguage]);

    const hasDescription = Boolean(
        product?.description?.trim() ||
        routeData.routeHighlights?.trim()
    );
    const displayMenuItems: MenuItems[] = useMemo(() => {
        if (propMenuItems && propMenuItems.length > 0) return propMenuItems;
        const items: MenuItems[] = [
            { title: t('route_information', 'Route Information'), link: '#information', svg: '' },
            ...(hasDescription ? [{ title: t('description', 'Description'), link: '#description', svg: '' }] as MenuItems[] : []),
            ...(routeDaysWithImages.length > 0 ? [{ title: t('route_program', 'Route Program'), link: '#itinerary', svg: '' }] as MenuItems[] : []),
            ...(hasMapLocations ? [{ title: t('map', 'Map'), link: '#map', svg: '' }] as MenuItems[] : []),
            ...(productFaqs?.length ? [{ title: t('faq', 'FAQ'), link: '#faqs', svg: '' }] as MenuItems[] : []),
        ];
        return items;
    }, [propMenuItems, hasDescription, routeDaysWithImages.length, hasMapLocations, productFaqs?.length, t]);

    return (
        <motion.div 
            className="w-9/12 mx-auto px-4 py-16 relative z-10 min-h-screen" 
            initial="hidden" 
            animate="visible" 
            variants={pageVariants}
        >
            <div className="sticky lg:top-48 z-[60] flex justify-end gap-3 mb-4">
                <motion.button
                    type="button"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={toggleFavorite}
                    className={`p-3 rounded-full shadow-lg transition-all ${
                        isFavorite 
                            ? 'bg-alsero-first text-white' 
                            : 'bg-white text-alsero-first hover:bg-gray-50'
                    }`}
                    title={isFavorite ? t('yacht_route_detail.remove_from_favorites', 'Favorilerden Çıkar') : t('yacht_route_detail.add_to_favorites', 'Favorilere Ekle')}
                >
                    <svg className="w-6 h-6" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                </motion.button>

                <ProductShareButton
                    url={typeof window !== 'undefined' ? window.location.href : ''}
                    title={product?.name || 'Yacht Route - Alsero Tours'}
                    translationPrefix="yacht_route_detail"
                />
            </div>

            {displayMenuItems.length > 0 && (
                <div className="sticky top-0 z-[60] mb-4 bg-white/95 py-2 backdrop-blur-sm sm:bg-transparent sm:py-0 sm:backdrop-blur-none">
                    <ProductDetailMenu links={displayMenuItems} />
                </div>
            )}

            <motion.div className="grid grid-cols-12 mt-8 gap-8" initial="hidden" animate="visible" variants={pageVariants}>
                <div className="col-span-12">
            {/* Hero Section */}
            <motion.section 
                        className="mb-[75px]"
                        variants={slideFromBottomVariants}
                    >
                        <motion.div variants={cardVariants}>
                            <h1 className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-4">
                                {product?.name || routeData.routeName || t('yacht_route', 'Yacht Route')}
                            </h1>
                            {breadcrumbs && breadcrumbs.length > 0 && (
                                <nav className="text-sm text-alsero-text-tour mb-6">
                                    {breadcrumbs.map((crumb, index) => (
                                        <span key={index}>
                                            {index > 0 && <span className="mx-2">/</span>}
                                            {crumb.link ? (
                                                <a href={crumb.link} className="hover:text-alsero-first transition-colors">
                                                    {crumb.title}
                                                </a>
                                            ) : (
                                                <span>{crumb.title}</span>
                                            )}
                                        </span>
                                    ))}
                                </nav>
                            )}
                        </motion.div>
                    </motion.section>

                    {/* Açıklama / Özet - müşteriyi sayfada tutar */}
                    {hasDescription && (
                        <motion.section
                            id="description"
                            className="mb-[75px] scroll-mt-20"
                            variants={slideFromBottomVariants}
                        >
                            <motion.div variants={cardVariants}>
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8">
                                    <h2 className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-6">
                                        {t('overview', 'Overview')}
                                    </h2>
                                    {(routeData.routeHighlights?.trim() || product?.description?.trim()) && (
                                        <div
                                            className="text-alsero-text-tour leading-relaxed prose prose-lg max-w-none"
                                            dangerouslySetInnerHTML={{
                                                __html: sanitizeCmsHtml(
                                                    transformHtmlImageUrls(
                                                    routeData.routeHighlights?.trim() ||
                                                    product?.description?.trim() ||
                                                    ''
                                                )),
                                            }}
                                        />
                                    )}
                                    {product?.description?.trim() && routeData.routeHighlights?.trim() && (
                                        <div
                                            className="text-alsero-text-tour leading-relaxed mt-4 prose prose-lg max-w-none"
                                            dangerouslySetInnerHTML={{
                                                __html: sanitizeCmsHtml(transformHtmlImageUrls(product.description.trim())),
                                            }}
                                        />
                                    )}
                                </div>
                            </motion.div>
                        </motion.section>
                    )}

                    {/* Route Information */}
                    {(() => {
                        const hasDurationDays = routeData.durationDays !== null;
                        const hasDurationHours = routeData.durationHours !== null;
                        const hasRouteType = !!routeData.routeType;
                        const hasStartLocation = !!routeData.startLocation;
                        const hasEndLocation = !!routeData.endLocation;
                        const hasIntro = !!(routeData.routeHighlights || product?.description);
                        const hasAnyInfo = hasDurationDays || hasDurationHours || hasRouteType || hasStartLocation || hasEndLocation || hasIntro;

                        if (!hasAnyInfo) return null;

                        return (
                            <motion.section 
                                id="information"
                                className="mb-[75px] scroll-mt-20"
                                variants={slideFromBottomVariants}
                            >
                                <motion.div variants={cardVariants}>
                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8">
                                        <h2 className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-6">
                                            {t('route_information', 'Route Information')}
                                        </h2>
                                        {(routeData.routeHighlights || product?.description) && (
                                            <p className="text-alsero-text-tour leading-relaxed mb-6">
                                                {routeData.routeHighlights || (typeof product?.description === 'string' ? product.description.replace(/<[^>]*>/g, '').trim().slice(0, 500) : '')}
                                            </p>
                                        )}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {hasDurationDays && (
                                                <div>
                                                    <span className="text-sm font-semibold text-alsero-dark-gray">{t('duration_days', 'Duration (Days)')}:</span>
                                                    <p className="text-alsero-text-tour">{routeData.durationDays} {t('days', 'days')}</p>
                                                </div>
                                            )}
                                            {hasDurationHours && (
                                                <div>
                                                    <span className="text-sm font-semibold text-alsero-dark-gray">{t('duration_hours', 'Duration (Hours)')}:</span>
                                                    <p className="text-alsero-text-tour">{routeData.durationHours} {t('hours', 'hours')}</p>
                                                </div>
                                            )}
                                            {hasRouteType && (
                                                <div>
                                                    <span className="text-sm font-semibold text-alsero-dark-gray">{t('route_type', 'Route Type')}:</span>
                                                    <p className="text-alsero-text-tour">{routeData.routeType}</p>
                                                </div>
                                            )}
                                            {hasStartLocation && (
                                                <div>
                                                    <span className="text-sm font-semibold text-alsero-dark-gray">{t('start_location', 'Start')}:</span>
                                                    <p className="text-alsero-text-tour">{routeData.startLocation}</p>
                                                </div>
                                            )}
                                            {hasEndLocation && (
                                                <div>
                                                    <span className="text-sm font-semibold text-alsero-dark-gray">{t('end_location', 'End')}:</span>
                                                    <p className="text-alsero-text-tour">{routeData.endLocation}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.section>
                        );
                    })()}

                    {/* Route Program */}
                    {routeDaysWithImages.length > 0 && (
                        <motion.section 
                            id="itinerary"
                            className="mb-[75px] scroll-mt-20"
                            variants={slideFromBottomVariants}
                        >
                            <motion.div 
                                className="flex items-center justify-between mb-8"
                                variants={headerVariants}
                            >
                                <h2 className="text-2xl lg:text-4xl font-bold text-alsero-dark-gray">
                                    {t('route_program', 'Route Program')}
                                </h2>
                                <button type="button"
                                    onClick={handleExpandAll}
                                    className="text-sm text-alsero-dark-gray underline hover:text-alsero-first transition-colors"
                                >
                                    {allExpanded ? t('collapse_all', 'Collapse All') : t('expand_all', 'Expand All')}
                                </button>
                            </motion.div>
                            
                            <motion.div variants={cardVariants} className={hasMapLocations ? 'lg:grid lg:grid-cols-5 lg:gap-8 lg:items-start' : 'mb-8'}>
                                <div className={hasMapLocations ? 'lg:col-span-2 min-w-0' : ''}>
                                <div className="relative">
                                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-300"></div>
                                    
                                    <div className="space-y-0">
                                        {routeDaysWithImages.map((day: any) => {
                                            const isOpen = allExpanded || openRouteDay === day.id;
                                            const isDayHighlighted = highlightedDayId === day.id;
                                            const hasMapForDay = dayFirstMapIndex.has(day.id);
                                            
                                            return (
                                                <motion.div
                                                    key={day.id}
                                                    data-day-id={day.id}
                                                    className={`relative flex gap-6 pb-8 last:pb-0 ${
                                                        isDayHighlighted ? 'rounded-xl ring-2 ring-alsero-first/50 bg-red-50/30 px-2 -mx-2' : ''
                                                    }`}
                                                    variants={cardVariants}
                                                >
                                                    <div className="flex-shrink-0 relative z-10">
                                                        <motion.button 
                                                            type="button"
                                                            onClick={() => {if (!allExpanded) {handleRouteDayToggle(day.id);}}} 
                                                            className="w-24 h-20 bg-alsero-first text-white cursor-pointer rounded-lg flex items-center justify-center font-bold text-lg shadow-md"
                                                        >
                                                            {String(day.dayNumber).padStart(2, '0')}
                                                        </motion.button>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-start gap-2">
                                                        <motion.button 
                                                            type="button"
                                                            onClick={() => {if (!allExpanded) {handleRouteDayToggle(day.id);}}} 
                                                            className="flex-1 min-w-0 text-left cursor-pointer" 
                                                            disabled={allExpanded}
                                                        >
                                                            <h3 className="font-bold text-alsero-dark-gray text-lg leading-relaxed mb-2 hover:text-alsero-first transition-colors">
                                                                {day.title}
                                                            </h3>
                                                        </motion.button>
                                                        {hasMapForDay && (
                                                            <button
                                                                type="button"
                                                                onClick={() => focusMapOnDay(day.id)}
                                                                className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-alsero-first/30 bg-white px-2 py-1 text-xs font-semibold text-alsero-first hover:bg-red-50"
                                                            >
                                                                {t('map_show_on_map', 'Show on map')}
                                                            </button>
                                                        )}
                                                        </div>
                                                        <AnimatePresence initial={false}>
                                                            {isOpen && (
                                                                <motion.div
                                                                    role="region"
                                                                    initial={{ 
                                                                        height: 0, 
                                                                        opacity: 0,
                                                                    }}
                                                                    animate={{ 
                                                                        height: "auto", 
                                                                        opacity: 1,
                                                                    }}
                                                                    exit={{ 
                                                                        height: 0, 
                                                                        opacity: 0,
                                                                    }}
                                                                    transition={{ 
                                                                        height: {
                                                                            type: "spring" as const,
                                                                            stiffness: 300,
                                                                            damping: 30,
                                                                            mass: 0.8
                                                                        },
                                                                        opacity: {
                                                                            duration: 0.3,
                                                                            ease: "easeInOut"
                                                                        }
                                                                    }}
                                                                    className="overflow-hidden"
                                                                >
                                                                    <motion.div 
                                                                        className="pt-2"
                                                                        initial={{ opacity: 0 }}
                                                                        animate={{ opacity: 1 }}
                                                                        exit={{ opacity: 0 }}
                                                                        transition={{ 
                                                                            duration: 0.2,
                                                                            delay: 0.1
                                                                        }}
                                                                    >
                                                                        {day.description && (
                                                                            <div 
                                                                                className="text-alsero-text-tour leading-relaxed mb-4 prose prose-lg max-w-none"
                                                                                dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(day.description) }}
                                                                            />
                                                                        )}
                                                                        {day.images && day.images.length > 0 && (
                                                                            <div className="grid grid-cols-3 gap-4 mt-4">
                                                                                {day.images.map((img: string, idx: number) => (
                                                                                    <div key={idx} className="aspect-video bg-gray-200 rounded-lg overflow-hidden">
                                                                                        <LazyImage
                                                                                            src={img}
                                                                                            alt={`${day.title} - Image ${idx + 1}`}
                                                                                            fill
                                                                                            className="object-cover"
                                                                                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 25vw"
                                                                                        />
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                        {day.activities && (
                                                                            <div className="mt-4">
                                                                                <span className="text-sm font-semibold text-alsero-dark-gray">{t('activities', 'Activities')}:</span>
                                                                                <p className="text-alsero-text-tour">{day.activities}</p>
                                                                            </div>
                                                                        )}
                                                                    </motion.div>
                                                                </motion.div>
                                                            )}
                                                        </AnimatePresence>
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                </div>
                                </div>

                                {hasMapLocations && (
                                    <div ref={mapSectionRef} className="lg:col-span-3 lg:sticky lg:top-24 lg:self-start mt-8 lg:mt-0">
                                        <motion.div
                                            id="map"
                                            className={`relative rounded-lg overflow-hidden w-full transition-all duration-500 scroll-mt-20 ${
                                                isStoryModeActive ? 'h-[28rem] md:h-[32rem] lg:h-[36rem]' : 'h-[28rem] lg:h-[32rem]'
                                            }`}
                                            variants={cardVariants}
                                        >
                                            <InteractiveMapStory
                                                mapRef={mapRef}
                                                points={mapStoryPoints}
                                                activeIndex={isStoryModeActive ? (mapActiveIndex ?? 0) : undefined}
                                                onActiveIndexChange={isStoryModeActive ? handleStoryIndexChange : undefined}
                                                onStoryModeChange={handleStoryModeChange}
                                            >
                                                <TourRouteMap
                                                    ref={mapRef}
                                                    className="w-full h-full rounded-lg"
                                                    isSidebarOpen={isStoryModeActive}
                                                    locations={mapLocations}
                                                    activeMarkerIndex={mapActiveIndex}
                                                    onMarkerClick={handleMapMarkerClick}
                                                    dayLabelTemplate={t('map_day_label', 'Day {day}')}
                                                    dayStopLabelTemplate={t('map_day_stop_label', 'Stop {current}/{total}')}
                                                />
                                            </InteractiveMapStory>
                                        </motion.div>
                                        <TourRouteMapLegend
                                            className="mt-3"
                                            startLabel={t('map_legend_start', 'Start')}
                                            stopLabel={t('map_legend_stop', 'Stop')}
                                            endLabel={t('map_legend_end', 'End')}
                                        />
                                    </div>
                                )}
                            </motion.div>
                        </motion.section>
                    )}

                    {/* FAQs */}
                    {productFaqs && productFaqs.length > 0 && (
                        <motion.section 
                            id="faqs"
                            className="mb-[75px] scroll-mt-20"
                            variants={slideFromBottomVariants}
                        >
                            <Faqs language={currentLanguage} generalFaqs={productFaqs} />
                        </motion.section>
                    )}

                    {/* Diğer Rotalar - daraltılmış genişlik, kısa açıklamalar */}
                    <motion.section
                        id="other-routes"
                        className="mb-[75px] scroll-mt-20"
                        variants={slideFromBottomVariants}
                    >
                        <div className="max-w-7xl mx-auto">
                            <SectionTitle
                                title={t('other_routes', 'Diğer Rotalar')}
                                titleTag="h2"
                                subTitle={t('other_routes_subtitle', 'Keşfetmeye devam edin')}
                                subTitleTag="span"
                                description={null}
                                descriptionTag={null}
                            />
                            {otherRoutesLoading && (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                                    {[1, 2, 3].map((i) => (
                                        <div key={i} className="h-80 rounded-lg bg-gray-100 animate-pulse" />
                                    ))}
                                </div>
                            )}
                            {!otherRoutesLoading && otherRoutes.length > 0 && (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                                    {otherRoutes.map((route, index) => (
                                        <YachtRouteCard
                                            key={route.id}
                                            route={route}
                                            index={index}
                                            isInView={true}
                                            language={currentLanguage}
                                            languageCode={currentLanguage}
                                            pageUrls={pageUrls}
                                            defaultLanguage={defaultLanguage}
                                        />
                                    ))}
                                </div>
                            )}
                            {!otherRoutesLoading && otherRoutes.length === 0 && (
                                <p className="text-alsero-text-tour mt-8 text-center">
                                    {t('yacht_routes_no_routes', 'Henüz başka rota bulunmuyor.')}
                                </p>
                            )}
                        </div>
                    </motion.section>
                </div>
            </motion.div>
        </motion.div>
    );
}

